import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect } from "react";

import appCss from "../styles.css?url";
import { Toaster } from "@/components/ui/sonner";
import { SAME_AS, OG_IMAGE_URL } from "@/lib/schema";
import { applyConsentDefaults } from "@/lib/consent";



function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Avolus | Luxury Car Rental" },
      { name: "description", content: "Avolus offers west coast car rentals: a curated fleet of exotic and ultra-luxury cars for rent. Drive extraordinary." },
      { name: "author", content: "Avolus" },
      { property: "og:title", content: "Avolus | Luxury Car Rental" },
      { property: "og:description", content: "Avolus offers a curated fleet of exotic and ultra-luxury cars for rent. Drive extraordinary." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Avolus | Luxury Car Rental" },
      { name: "twitter:description", content: "Avolus offers a curated fleet of exotic and ultra-luxury cars for rent. Drive extraordinary." },
      { property: "og:image", content: OG_IMAGE_URL },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Avolus Cars, luxury car rental in Vancouver" },
      { name: "twitter:image", content: OG_IMAGE_URL },
      { name: "google-site-verification", content: "99fUrZmLTSowS5h6bFDW43izPlDQ8U6zAw1Sxnk8B-4" },
    ],
    links: [
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/favicon.png" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/favicon.png" },
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "https://fonts.gstatic.com/s/inter/v20/UcC73FwrK3iLTeHuS_nVMrMxCp50SjIa1ZL7W0Q5nw.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "https://fonts.gstatic.com/s/cormorantgaramond/v21/co3bmX5slCNuHLi8bLeY9MK7whWMhyjYqXtKky2F7g.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=Inter:wght@300;400;500;600&display=swap",
        media: "print",
        onLoad: "this.media='all'",
      } as any,

    ],
    scripts: [
      {

        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AutoRental",
          "@id": "https://avoluscars.com/#business",
          name: "Avolus Luxury Car Rental",
          alternateName: "Avolus Cars",
          legalName: "1590976 B.C. LTD.",
          image: OG_IMAGE_URL,
          url: "https://avoluscars.com/",
          telephone: "+1-778-379-8881",
          email: "info@avoluscars.com",
          priceRange: "$200-$395 CAD/day",
          currenciesAccepted: "CAD",
          paymentAccepted: "Visa, Mastercard, American Express, PayPal, Interac e-Transfer, Cash, Bitcoin, Ethereum",
          address: {
            "@type": "PostalAddress",
            streetAddress: "250 - 997 Seymour St #1711",
            addressLocality: "Vancouver",
            addressRegion: "BC",
            postalCode: "V6B 3M1",
            addressCountry: "CA",
          },
          areaServed: [
            "Vancouver",
            "Metro Vancouver",
            "Richmond",
            "Burnaby",
            "North Vancouver",
            "West Vancouver",
            "Surrey",
            "YVR Airport",
            "Whistler",
          ],
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              opens: "08:00",
              closes: "20:00",
            },
          ],
          sameAs: SAME_AS,
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if ((window as any).__gaLoaded) return;

    let loaded = false;
    const load = () => {
      if (loaded) return;
      loaded = true;
      (window as any).__gaLoaded = true;
      (window as any).dataLayer = (window as any).dataLayer || [];
      function gtag(...args: any[]) {
        (window as any).dataLayer.push(args);
      }
      (window as any).gtag = gtag;
      // Consent Mode v2 defaults must be pushed before config.
      applyConsentDefaults();
      gtag("js", new Date());
      gtag("config", "G-F7Y4C5MP9W");

      const s = document.createElement("script");
      s.async = true;
      s.src = "https://www.googletagmanager.com/gtag/js?id=G-F7Y4C5MP9W";
      document.head.appendChild(s);
      cleanup();
    };

    const events: (keyof WindowEventMap)[] = ["scroll", "keydown", "pointerdown", "touchstart"];
    const cleanup = () => {
      events.forEach((e) => window.removeEventListener(e, load));
    };
    events.forEach((e) => window.addEventListener(e, load, { once: true, passive: true }));

    const idle = (window as any).requestIdleCallback
      ? (window as any).requestIdleCallback(load, { timeout: 1500 })
      : window.setTimeout(load, 1200);

    return () => {
      cleanup();
      if ((window as any).cancelIdleCallback) (window as any).cancelIdleCallback(idle);
      else window.clearTimeout(idle);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <Toaster richColors position="top-center" />
    </QueryClientProvider>
  );
}

